import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVolver } from '@/shared/hooks/useVolver';
import { productsApi, type LabelExtraction } from '@/shared/api/endpoints';
import { describeError } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, Field, Segmented, Spinner } from '@/shared/ui';
import { BackIcon, CameraIcon } from '@/shared/ui/icons';

function initial(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : value.toString();
}

/**
 * CU-12 A2 · aportar un producto al catálogo compartido.
 *
 * El orden de la pantalla es el argumento entero: primero la foto del rótulo, después los números,
 * y el nombre al final. Al revés —un formulario en blanco con un adjunto opcional— cargar una foto
 * sería más fácil que cargar datos, y eso es exactamente lo que dejó a Open Food Facts con 13.568
 * fichas argentinas con foto y sin un solo valor nutricional.
 *
 * Acá la foto no acompaña al dato: la foto ES de dónde sale el dato.
 */
export function ContributeProductPage() {
  const navigate = useNavigate();
  const volver = useVolver('/productos/buscar');

  const [labelKey, setLabelKey] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<LabelExtraction | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gallery = useRef<HTMLInputElement>(null);

  const leer = async (file: File) => {
    setReading(true);
    setError(null);

    try {
      const result = await productsApi.readLabelPhoto(file);
      setLabelKey(result.storageKey);
      setExtraction(result.extraction);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setReading(false);
    }
  };

  return (
    <>
      <header className="row-between">
        <button type="button" className="btn-square" aria-label="Volver" onClick={volver}>
          <BackIcon size={18} />
        </button>
        <h1 style={{ fontSize: 'var(--text-h2)' }}>Aportar un producto</h1>
        <span style={{ width: 38 }} />
      </header>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      {labelKey === null ? (
        <>
          <Card size="tight">
            <strong className="small">Empezá por la foto del rótulo</strong>
            <span className="micro muted">
              Los valores salen de ahí, no de lo que escribas. Sacale una foto a la tabla de
              información nutricional del envase.
            </span>
          </Card>

          {reading ? (
            <Spinner label="Leyendo el rótulo" />
          ) : (
            <Button variant="primary" block onClick={() => gallery.current?.click()}>
              <CameraIcon size={18} /> Sacar o elegir la foto
            </Button>
          )}

          {/*
            Un solo botón y sin `capture`. El selector nativo del teléfono ya ofrece las dos cosas
            —cámara y galería—, y poner `capture="environment"` haría lo contrario de lo que suena:
            esconde la galería. Es la misma decisión que ya está tomada en CapturePage.

            Sin HEIC en el `accept`: la imagen existe para que un modelo la lea, y el modelo no
            entiende HEIC. Dejarla pasar haría que la subida saliera bien y la lectura volviera
            vacía, sin que nadie pudiera explicar por qué.
          */}
          <input
            ref={gallery}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];

              if (file !== undefined) {
                void leer(file);
              }
            }}
          />
        </>
      ) : (
        <ContributeForm
          labelKey={labelKey}
          extraction={extraction}
          onPublished={() => navigate('/productos/buscar', { replace: true })}
          onOtraFoto={() => {
            setLabelKey(null);
            setExtraction(null);
          }}
        />
      )}
    </>
  );
}

function ContributeForm({
  labelKey,
  extraction,
  onPublished,
  onOtraFoto,
}: {
  labelKey: string;
  extraction: LabelExtraction | null;
  onPublished: () => void;
  onOtraFoto: () => void;
}) {
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [description, setDescription] = useState('');
  const [ean, setEan] = useState('');

  const [kcal, setKcal] = useState(initial(extraction?.kcal));
  const [proteinG, setProteinG] = useState(initial(extraction?.proteinG));
  const [carbohydratesG, setCarbohydratesG] = useState(initial(extraction?.carbohydratesG));
  const [fatG, setFatG] = useState(initial(extraction?.fatG));
  const [fiberG, setFiberG] = useState(initial(extraction?.fiberG));
  const [sodiumMg, setSodiumMg] = useState(initial(extraction?.sodiumMg));
  const [perServing, setPerServing] = useState(extraction?.perServing ?? false);
  const [servingWeightG, setServingWeightG] = useState(initial(extraction?.servingWeightG));

  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete =
    name.trim() !== '' &&
    kcal !== '' &&
    proteinG !== '' &&
    carbohydratesG !== '' &&
    fatG !== '' &&
    (!perServing || servingWeightG !== '');

  const numeros = [
    { key: 'kcal', label: 'Calorías', unit: 'kcal', value: kcal, set: setKcal },
    { key: 'proteinG', label: 'Proteína', unit: 'g', value: proteinG, set: setProteinG },
    {
      key: 'carbohydratesG',
      label: 'Carbohidratos',
      unit: 'g',
      value: carbohydratesG,
      set: setCarbohydratesG,
    },
    { key: 'fatG', label: 'Grasas', unit: 'g', value: fatG, set: setFatG },
    { key: 'fiberG', label: 'Fibra', unit: 'g', value: fiberG, set: setFiberG },
    { key: 'sodiumMg', label: 'Sodio', unit: 'mg', value: sodiumMg, set: setSodiumMg },
  ] as const;

  /*
    El catálogo guarda todo por 100 g (RN-05). Una lectura por porción se convierte acá y no en el
    servidor porque el servidor recibe el producto ya normalizado: el comando de publicación
    describe una fila del catálogo, no un rótulo.
  */
  const aCien = (value: string): number => {
    const number = Number(value);

    if (!perServing) {
      return number;
    }

    return (number * 100) / Number(servingWeightG);
  };

  const publicar = async () => {
    setPublishing(true);
    setError(null);

    try {
      await productsApi.publishFromLabel({
        name: name.trim(),
        brand: brand.trim() === '' ? null : brand.trim(),
        ean: ean.trim() === '' ? null : ean.trim(),
        description: description.trim() === '' ? null : description.trim(),
        declaredKcal: Math.round(aCien(kcal)),
        macros100g: {
          proteinG: aCien(proteinG),
          carbohydratesG: aCien(carbohydratesG),
          fatG: aCien(fatG),
          fiberG: fiberG === '' ? null : aCien(fiberG),
          sodiumMg: sodiumMg === '' ? null : aCien(sodiumMg),
          alcoholG: null,
        },
        servingWeightG: perServing ? Number(servingWeightG) : null,
        labelStorageKey: labelKey,
      });

      onPublished();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <>
      <Card size="tight">
        <strong className="small">
          {extraction !== null && extraction.extractionAvailable
            ? 'Revisá lo que se leyó'
            : 'Cargá los valores del envase'}
        </strong>
        <span className="micro muted">
          {extraction !== null && extraction.extractionAvailable
            ? 'La foto quedó guardada como respaldo de estos números.'
            : 'No se pudo leer la tabla. La foto quedó guardada igual.'}
        </span>
        <Button variant="plain" onClick={onOtraFoto}>
          Sacar otra foto
        </Button>
      </Card>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Segmented
        options={[
          { value: 'cien', label: 'Por 100 g' },
          { value: 'porcion', label: 'Por porción' },
        ]}
        value={perServing ? 'porcion' : 'cien'}
        onChange={(value) => setPerServing(value === 'porcion')}
        label="Base de los valores"
      />

      {perServing && (
        <Field
          label="Peso de la porción (g)"
          hint="Sin esto no se puede llevar los valores a 100 g."
          type="number"
          inputMode="decimal"
          value={servingWeightG}
          onChange={(event) => setServingWeightG(event.target.value)}
        />
      )}

      <Card size="flush">
        {numeros.map((campo) => (
          <div key={campo.key} className="option-row">
            <span className="item-body">
              <span className="small">{campo.label}</span>
            </span>
            <span className="item-value">
              <input
                type="number"
                inputMode="decimal"
                aria-label={campo.label}
                value={campo.value}
                onChange={(event) => campo.set(event.target.value)}
                style={{ border: 'none', textAlign: 'right', width: 88 }}
              />
              <span className="nano muted">{campo.unit}</span>
            </span>
          </div>
        ))}
      </Card>

      <Field
        label="Nombre del producto"
        hint="Como figura en el envase."
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Galletitas de agua"
      />
      <Field
        label="Marca (opcional)"
        value={brand}
        onChange={(event) => setBrand(event.target.value)}
        placeholder="Criollitas"
      />
      <Field
        label="Descripción (opcional)"
        hint="Lo que distingue a este de uno parecido: «sin TACC», «el de la lata chica»."
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <Field
        label="Código de barras (opcional)"
        hint="Si lo cargás, quien lo escanee va a encontrar este producto."
        inputMode="numeric"
        value={ean}
        onChange={(event) => setEan(event.target.value)}
      />

      <div className="sticky-foot">
        <Button
          variant="primary"
          block
          onClick={() => void publicar()}
          disabled={publishing || !complete}
        >
          {publishing ? 'Publicando…' : 'Publicarlo en el catálogo'}
        </Button>
        <p className="micro muted">
          Cualquiera va a poder encontrarlo. Podés retirarlo después desde la búsqueda.
        </p>
      </div>
    </>
  );
}
